/**
 * ========================================
 * ANIMATED GRID PATTERN COMPONENT
 * ========================================
 * 
 * Animated grid pattern background component converted from TypeScript/Tailwind
 * to JavaScript/MUI for use in the hero section.
 * 
 * Features:
 * - Animated squares that fade in/out
 * - Responsive to container size
 * - Customizable opacity, duration, and square count
 */

import React, { useEffect, useId, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Box } from "@mui/material";

const AnimatedGridPattern = ({
  width = 40,
  height = 40,
  x = -1,
  y = -1,
  strokeDasharray = 0,
  numSquares = 50,
  maxOpacity = 0.5,
  duration = 4,
  repeatDelay = 0.5,
  sx = {},
  ...props
}) => {
  const id = useId();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [squares, setSquares] = useState(() => []);

  function getPos() {
    if (dimensions.width === 0 || dimensions.height === 0) {
      return [0, 0];
    }
    return [
      Math.floor((Math.random() * dimensions.width) / width),
      Math.floor((Math.random() * dimensions.height) / height),
    ];
  }

  // Generate squares with id, x, and y positions
  function generateSquares(count) {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      pos: getPos(),
    }));
  }

  // Function to update a single square's position
  const updateSquarePosition = (squareId) => {
    setSquares((currentSquares) =>
      currentSquares.map((sq) =>
        sq.id === squareId
          ? {
              ...sq,
              pos: getPos(),
            }
          : sq,
      ),
    );
  };

  // Update squares to animate in when dimensions are available
  useEffect(() => {
    if (dimensions.width && dimensions.height) {
      setSquares(generateSquares(numSquares));
    }
  }, [dimensions, numSquares, width, height]);

  // Resize observer to update container dimensions
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <Box
      component="svg"
      ref={containerRef}
      aria-hidden="true"
      sx={{
        pointerEvents: "none",
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        fill: "rgba(255, 255, 255, 0.15)",
        stroke: "rgba(255, 255, 255, 0.2)",
        overflow: "visible",
        ...sx,
      }}
      {...props}
    >
      <defs>
        <pattern
          id={id}
          width={width}
          height={height}
          patternUnits="userSpaceOnUse"
          x={x}
          y={y}
        >
          <path
            d={`M.5 ${height}V.5H${width}`}
            fill="none"
            strokeDasharray={strokeDasharray}
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
      <svg x={x} y={y} style={{ overflow: "visible" }}>
        {squares.map(({ pos: [posX, posY], id: squareId }, index) => (
          <motion.rect
            key={`${posX}-${posY}-${index}-${squareId}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: maxOpacity }}
            transition={{
              duration,
              repeat: 1,
              delay: index * 0.1,
              repeatType: "reverse",
            }}
            onAnimationComplete={() => updateSquarePosition(squareId)}
            width={width - 1}
            height={height - 1}
            x={posX * width + 1}
            y={posY * height + 1}
            fill="currentColor"
            strokeWidth="0"
          />
        ))}
      </svg>
    </Box>
  );
};

export default AnimatedGridPattern;

